import React, { useState } from 'react';
import { PlayCircle } from 'lucide-react';
import { Story } from './types';
import { formatViews, formatTimeAgo } from './utils/videoUtils';
import Avatar from './Avatar';

/**
 * A story's picture, never cropped through a face.
 *
 * Korean Beauty (21 Sep 2026): the stories "don't give the vibe of
 * attraction". Their stories are portrait phone videos, and every card was a
 * wide 16:9 box filled edge to edge, which keeps the middle band of a tall
 * frame — the presenter's chest — and cuts the head off. Now: a picture that
 * matches the frame fills it (leaning to the upper part, where a face is);
 * one that doesn't sits whole on a soft blur of itself, the way Instagram
 * shows a landscape post in a portrait feed.
 */
export function StoryThumb({
  src,
  alt,
  frame,
  className = '',
}: {
  src: string;
  alt: string;
  frame: 'portrait' | 'landscape';
  className?: string;
}) {
  const [shape, setShape] = useState<'portrait' | 'landscape' | 'square' | null>(null);
  const [broken, setBroken] = useState(false);
  const usable = !!src && src.startsWith('http') && !broken;
  const mismatch = (frame === 'portrait' && shape === 'landscape') || (frame === 'landscape' && shape === 'portrait');

  if (!usable) {
    return (
      <div className="absolute inset-0 bg-gradient-to-br from-slate-700 to-slate-900 flex items-center justify-center">
        <div className="text-center text-white p-4">
          <PlayCircle className="w-12 h-12 mx-auto mb-2 opacity-50" />
          <p className="text-xs opacity-75">Thumbnail unavailable</p>
        </div>
      </div>
    );
  }
  return (
    <>
      {mismatch && (
        // eslint-disable-next-line @next/next/no-img-element
        <img src={src} alt="" aria-hidden className="absolute inset-0 w-full h-full object-cover scale-125 blur-2xl opacity-80" />
      )}
      {/* eslint-disable-next-line @next/next/no-img-element */}
      <img
        src={src}
        alt={alt}
        loading="lazy"
        onLoad={(e) => {
          const { naturalWidth: w, naturalHeight: h } = e.currentTarget;
          setShape(h > w * 1.1 ? 'portrait' : w > h * 1.1 ? 'landscape' : 'square');
        }}
        onError={() => setBroken(true)}
        className={`absolute inset-0 w-full h-full transition-transform duration-500 ${mismatch ? 'object-contain' : 'object-cover'} ${className}`}
        style={{ objectPosition: '50% 28%' }}
      />
    </>
  );
}

/**
 * Portrait tiles when a brand's stories are phone videos (uploaded ones, as
 * Korean Beauty's are); the wide cards when they are mostly YouTube links.
 */
export function storyFrameFor(stories: { videoType?: string }[]): 'portrait' | 'landscape' {
  const linked = stories.filter((s) => s.videoType === 'youtube').length;
  return linked > stories.length / 2 ? 'landscape' : 'portrait';
}

interface StoryCardProps {
  story: Story;
  onClick: (story: Story) => void;
  compact?: boolean;
  /** The grid's frame: tall tiles for phone videos, wide cards for YouTube. */
  frame?: 'portrait' | 'landscape';
}

const StoryCard: React.FC<StoryCardProps> = ({ story, onClick, compact = false, frame = 'landscape' }) => {
  const duration = story.duration && story.duration !== '0:00' ? story.duration : null;

  // "Up next" beside the player: a small wide picture and the words beside it.
  if (compact) {
    return (
      <div className="group cursor-pointer flex flex-row gap-3 mb-3" onClick={() => onClick(story)}>
        <div className="relative overflow-hidden rounded-xl bg-slate-900 w-40 min-w-[160px] aspect-video">
          <StoryThumb src={story.thumbnailUrl} alt={story.title} frame="landscape" />
          {duration && (
            <div className="absolute bottom-1 right-1 bg-black/80 text-white text-xs px-1.5 py-0.5 rounded font-medium">{duration}</div>
          )}
        </div>
        <div className="flex flex-col flex-1 justify-start min-w-0">
          <h3 className="font-semibold text-slate-900 leading-tight line-clamp-2 text-sm">{story.title}</h3>
          <div className="text-slate-500 mt-1 text-xs">
            {story.author} • {formatTimeAgo(story.uploadDate)}
          </div>
          <div className="text-xs text-slate-500 mt-0.5">{formatViews(story.views)} views</div>
        </div>
      </div>
    );
  }

  // A tall tile: the picture is the card, the words sit on it.
  if (frame === 'portrait') {
    return (
      <button
        type="button"
        className="group relative block w-full overflow-hidden rounded-2xl bg-slate-900 aspect-[9/16] text-left shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-900 focus-visible:ring-offset-2"
        onClick={() => onClick(story)}
        aria-label={`Play ${story.title}`}
      >
        <StoryThumb src={story.thumbnailUrl} alt={story.title} frame="portrait" className="group-hover:scale-[1.04]" />
        <div className="absolute inset-x-0 bottom-0 h-2/5 bg-gradient-to-t from-black/85 via-black/40 to-transparent" />
        {duration && (
          <div className="absolute top-2 right-2 bg-black/55 backdrop-blur-sm text-white text-[11px] px-2 py-0.5 rounded-full font-semibold">
            {duration}
          </div>
        )}
        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
          <PlayCircle className="w-14 h-14 text-white drop-shadow-lg" />
        </div>
        <div className="absolute inset-x-0 bottom-0 p-3">
          <h3 className="text-white font-semibold text-[13.5px] leading-snug line-clamp-2 drop-shadow">{story.title}</h3>
          <p className="text-white/75 text-[11.5px] mt-1">
            {formatViews(story.views)} views · {formatTimeAgo(story.uploadDate)}
          </p>
        </div>
      </button>
    );
  }

  return (
    <div className="group cursor-pointer flex flex-col gap-2 mb-6" onClick={() => onClick(story)}>
      <div className="relative overflow-hidden rounded-xl bg-slate-900 w-full aspect-video">
        <StoryThumb src={story.thumbnailUrl} alt={story.title} frame="landscape" className="group-hover:scale-105" />
        {/* A linked YouTube/TikTok story has no duration of ours, and a badge
            reading "0:00" on a six-minute video is a lie. */}
        {duration && (
          <div className="absolute bottom-1 right-1 bg-black/80 text-white text-xs px-1.5 py-0.5 rounded font-medium">{duration}</div>
        )}
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors flex items-center justify-center">
          <PlayCircle className="text-white opacity-0 group-hover:opacity-100 transition-opacity w-12 h-12 drop-shadow-lg" />
        </div>
      </div>
      <div className="flex gap-3 items-start">
        <Avatar src={story.authorAvatar} name={story.author} size="md" className="mt-0.5" />
        <div className="flex flex-col flex-1 min-w-0">
          <h3 className="font-semibold text-slate-900 leading-tight line-clamp-2 text-base">{story.title}</h3>
          <div className="text-slate-500 mt-1 flex flex-wrap items-center gap-1 text-sm">
            <span>{story.author}</span>
            <span>•</span>
            <span>{formatViews(story.views)} views</span>
            <span>•</span>
            <span>{formatTimeAgo(story.uploadDate)}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StoryCard;
